/* eslint-disable */
import Axios from 'axios';
import axiosRetry from 'axios-retry';
//import { cacheAdapterEnhancer } from 'axios-extensions';
import { extLocalStore } from 'util/localStorage';
import { sendToLogger } from 'logger';
import { getAuth0Token } from './auth0';
import config from 'config';
import { store } from 'App';
import { userActions } from 'redux/actions';

// eslint-disable-next-line id-match
import { CLOUD_ENV, cloudEnvs, cacheKey, postBodyEndpoints, resource } from 'appConstants';

export const Headers = {
	SessionToken: 'x-session-token',
	Authorization: 'Authorization',
	UserInfo: 'X-USERINFO'
};

/* eslint camelcase: ["error", {allow: ["client_ver", "app_name"]}]*/
export const axios = Axios.create({
	baseURL: config.apiBaseUrl,
	responseType: 'json',
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json'
	},
	params: {
		client_ver: '2.0.0',
		app_name: 'ext',
		product: 'TextRecruit'
	}
	// We have plans to implement a better caching strategy in the future. See: https://jira.icims.net/browse/TXTE-26524
	//adapter: cacheAdapterEnhancer(Axios.defaults.adapter)
});

export function clearDefaults() {
	delete axios.defaults.params.account_id;
	delete axios.defaults.headers[Headers.SessionToken];
	delete axios.defaults.headers[Headers.Authorization];
	delete axios.defaults.headers[Headers.UserInfo];
	// TODO: add comment for clarifications
	extLocalStore.auth0 = false;
	extLocalStore.onLogin = false;
	extLocalStore.clear();
}

/**
 * This wrapper will retry any http-request that fails due to network/server errors.
 */
axiosRetry(axios, {
	retries: 4,
	retryCondition: (error) => {
		if (
			error &&
			error.message === 'Network Error' &&
			error.config &&
			error.config.url.includes('lookup.')
		) {
			return false;
		}
	},
	// eslint-disable-next-line no-magic-numbers
	retryDelay: () => 1000
});

/**
 * Updates API Urls to selected cloud provider.
 * All default values have been set in app/config/app.config.js
 * @param {String} cloudEnvironment -> cloud providers: aws, azure
 */
export const updateApiBaseUrl = ({ cloudEnvironment, cacheIt = true }) => {
	let apiRoot = config.apiRootUrl;
	let rtPath = config.realTimeUrl;
	if (cloudEnvironment.startsWith(cloudEnvs.AZURE)) {
		apiRoot = `https://api-azure-us.${config.envShortName}.textrecruit.tools`;
		rtPath = `wss://rt-azure-us.${config.envShortName}.textrecruit.tools/ws`;
	} else if (cloudEnvironment.startsWith(cloudEnvs.GOV)) {
		apiRoot = 'https://fed1.textrecruit.com';
		rtPath = 'wss://rt-awsgov-us.prd.textrecruit.tools/ws';
	} else if (cloudEnvironment.startsWith(cloudEnvs.EU)) {
		apiRoot = 'https://api-aws-eu.prd.textrecruit.tools';
		rtPath = 'wss://rt-aws-eu.prd.textrecruit.tools/ws';
	}

	/*
	 * Update:
	 *   - Axios base URL
	 *   - Real-time server url, and
	 *   - localStorage.ext.apiRoot/rtPath
	 */
	axios.defaults.baseURL = `${apiRoot}/api/v1/textrecruit`;
	config.realTimeUrl = rtPath;
	if (cacheIt) {
		extLocalStore.setItem(CLOUD_ENV, cloudEnvironment);
	}
};

export const getAccountId = (customerIds) => {
	try {
		return customerIds.find((c) => c.product === 'TextRecruit').customerId;
	} catch (e) {
		sendToLogger({
			level: 'error',
			message: {
				message: 'Error retrieving account ID',
				customerIds,
				err: e
			}
		});
		return null;
	}
};

/**
 * Function will retrieve and return either:
 * 1. JWT for legacy-authed user or SSO user; or
 * 2. Auth0 Access-token and id-token for IAM user
 *
 * If token is expired/removed user's session will be nulled.
 */
export async function getToken(request) {
	const resp = { accessToken: null, idToken: null, jwt: null };
	if (extLocalStore.auth0) {
		try {
			const { accessToken, idToken } = await getAuth0Token();
			resp.accessToken = accessToken;
			resp.idToken = idToken;
			if (request) {
				request.headers[Headers.Authorization] = `Bearer ${accessToken}`;
				request.headers[Headers.UserInfo] = idToken;
				delete request.headers[Headers.SessionToken];
			}
		} catch (err) {
			store.dispatch(userActions.reset());
			clearDefaults();
			return Promise.reject(err);
		}
	} else {
		const jwt = extLocalStore.getItem(cacheKey.TOKEN);
		if (!jwt) {
			store.dispatch(userActions.reset());
			clearDefaults();
			return Promise.reject({ message: 'token invalidated.' });
		}
		if (request) {
			request.headers[Headers.SessionToken] = jwt;
			delete request.headers[Headers.Authorization];
			delete request.headers[Headers.UserInfo];
		}
		resp.jwt = jwt;
	}
	return resp;
}

axios.interceptors.request.use(
	async (conf) => {
		// For specific POST endpoints, move account_id/customer_id from URL params to request body
		if (conf.method === 'post' && postBodyEndpoints.has(conf.url)) {
			if (conf.params) {
				if (conf.params.account_id && conf.data) {
					conf.data.account_id = conf.data.account_id || conf.params.account_id;
				}
				delete conf.params.account_id;
				delete conf.params.customer_id;
			}
		}

		if (
			conf.url.includes('lookup.') ||
			conf.url.includes('/remote_auth_info') ||
			conf.url.includes('/authenticate') ||
			conf.url.includes(resource.TRACK_SESSION)
		) {
			return conf;
		}
		// Set http headers with required token(s)
		await getToken(conf);
		return conf;
	},
	(error) => Promise.reject(error)
);

axios.interceptors.response.use(
	(response) => {
		const { url } = response.config;
		const { token } = response.data;
		const postLogin = url.includes('/authenticate');
		const postSession = url.includes('/login');
		const postLogout = url.includes('/logout');

		if (postLogin && !extLocalStore.auth0) {
			extLocalStore.setItem(cacheKey.TOKEN, token);
		}

		if (postLogin || postSession) {
			let { customerIds, userName } = response.data;
			if (postSession) {
				customerIds = response.data.userInfo.customerIds;
				userName = response.data.userInfo.userName;
			}
			extLocalStore.username = userName;
			const accountId = getAccountId(customerIds);
			axios.defaults.params.account_id = accountId;
			extLocalStore.accountId = accountId;
		}
		if (postLogout) {
			clearDefaults();
		}
		return response.data;
	},
	(error) => {
		const errorResp = error.response;
		if (errorResp && errorResp !== 400) {
			const url = error.config.url;
			const isLoggerError =
				url.includes(resource.TRACK_SESSION) || url.includes(resource.TRACK_ACTIVE);
			// To prevent infinite loop of logging errors when logger API call fails
			if (!isLoggerError) {
				sendToLogger({
					level: 'error',
					message: {
						error: errorResp.data,
						userAgent: navigator.userAgent,
						params: error.config.params
					}
				});
			}
		}

		if (error.message === 'Network Error') {
			return Promise.reject(error.toJSON());
		} else if (errorResp) {
			let errorData = {};
			const { data } = errorResp;
			if (typeof data === 'string') {
				errorData.message = data;
			} else if (data === null) {
				errorData.message = 'Error';
			} else if (data.apiResult) {
				errorData = { ...data.apiResult };
			} else {
				errorData = { ...data };
			}
			return Promise.reject(errorData);
		}
		return Promise.reject(error);
	}
);
